import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import {
  AppCard,
  AppDeleteModal,
  AppInput,
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
} from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import EnvironmentalAspectsAssessmentSidebar from '../../../layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/Sidebar/EnvironmentalAspectsAssessmentSidebar';
import AppSpinner from '../../../layout/components/AppSpinner';
import AppLink from '../../../routing/helpers/AppLink';
import EnvironmentalAspectsAssessmentBase from './EnvironmentalAspectsAssessmentBase';
import LoopModal from '@/layout/components/helpers/LoopModal';

export default function EnvironmentalAspectsAssessmentAspectLogListScreen() {
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const assessments = useSelector((s) => s?.currentPage?.assessments);
  const [loading, setLoading] = useState(true);
  const [breadcrumb, setBreadcrumb] = useState();
  const [editModal, setEditModal] = useState();
  const [deleteModal, setDeleteModal] = useState();
  const [handleDownloadXls, setHandleDownloadXls] = useState();

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD ASSESSMENT
  const loadAssessments = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_environmentalAspectsAssessment_assessment_log_list',
          ),
          {
            moduleBridgeHex,
            plantHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change('assessments', data, true));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, plantHex],
  );

  useEffect(() => {
    loadAssessments();
  }, []);
  // < LOAD ASSESSMENT

  // < EDIT
  const handleEdit = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_environmentalAspectsAssessment_assessment_log_edit',
          ),
          {
            moduleBridgeHex,
            plantHex,
            assessmentHex: editModal?.hex,
            ...payload,
          },
        )
        .then(() => {
          if (isMounted.current) {
            setEditModal();
            setLoading(true);
            loadAssessments();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantHex, editModal],
  );
  // > EDIT

  // < DELETE
  const handleDelete = useCallback(
    async () =>
      axios
        .delete(
          axiosPath(
            'api_r_user_module_environmentalAspectsAssessment_assessment_log_delete',
          ),
          {
            data: {
              moduleBridgeHex,
              plantHex,
              assessmentHex: deleteModal,
            },
          },
        )
        .then(() => {
          if (isMounted.current) {
            setDeleteModal();
            setLoading(true);
            loadAssessments();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantHex, deleteModal],
  );
  // > DELETE

  useEffect(() => {
    if (moduleData) {
      const b = [
        { name: moduleData?.moduleName },
        { name: 'Histórico de evaluaciones' },
      ];

      if (moduleData?.plant) {
        b.unshift({
          name: moduleData?.plant.name,
          route: {
            name: 'user_module_legalCompliance_evaluation',
            params: {
              moduleBridgeHex: moduleData?.plant?.moduleBridgeHex,
              plantHex,
              page: 1,
              filtersHex: 0,
            },
          },
        });
      }

      setBreadcrumb(b);
    } else {
      setBreadcrumb([{ name: '...' }]);
    }
  }, [moduleData]);

  //   const handleDownloadXls = (assessmentHex) =>
  //     axios
  //       .post(
  //         axiosPath('api_r_user_module_environmentalAspectsAssessment_assessment_xls'),
  //         {
  //           moduleBridgeHex,
  //           assessmentHex,
  //         },
  //       )
  //       //   .then(({ data }) => (window.location = data))
  //       .then(({ data }) => console.log('FINISH'))
  //       .catch((e) => dispatch(errorHandler(e)));

  return (
    <>
      <EnvironmentalAspectsAssessmentBase />
      <EnvironmentalAspectsAssessmentSidebar
        activeRoute="user_module_environmentalAspectsAssessment_assessment_log_list"
        assessmentTitle={moduleData?.assessmentTitle}
      />
      <BreadcrumbHandler paths={breadcrumb} />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <AppCard>
            {assessments?.length > 0 ? (
              <>
                <h4 className="mb-4">Histórico de evaluaciones</h4>
                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <thead>
                      <tr>
                        <th className="col-6 col-lg-9">Nombre</th>
                        <th className="col-2 col-lg-1">Estado</th>
                        <th className="col-2 col-lg-1">Fecha</th>
                        <th className="col-2 col-lg-1" />
                      </tr>
                    </thead>
                    <tbody>
                      {assessments.map((a) => (
                        <tr key={a.hex}>
                          <td>
                            <AppLink
                              path="user_module_environmentalAspectsAssessment_assessment_home"
                              params={{
                                moduleBridgeHex,
                                plantHex,
                                assessmentHex: a.hex,
                              }}
                            >
                              {a.title}
                            </AppLink>
                          </td>
                          <td>
                            <span
                              className={`badge badge-${
                                a.closed ? 'danger' : 'success'
                              }`}
                            >
                              {a.closed ? 'Cerrada' : 'Abierta'}
                            </span>
                          </td>
                          <td>{a.dateStamp}</td>
                          <td>
                            <button
                              className="btn btn-danger btn-xs float-right"
                              type="button"
                              title="Eliminar"
                              onClick={() => setDeleteModal(a.hex)}
                            >
                              <AppRemixIcon icon="delete-bin-6" />
                            </button>
                            <button
                              className="btn btn-info btn-xs float-right mr-2"
                              type="button"
                              title="Editar"
                              onClick={() => setEditModal(a)}
                            >
                              <AppRemixIcon icon="edit" />
                            </button>
                            <button
                              className="btn btn-success btn-xs float-right mr-2"
                              type="button"
                              title="Descargar XLS"
                              onClick={() => setHandleDownloadXls(a.hex)}
                            >
                              <AppRemixIcon icon="file" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {handleDownloadXls && (
                  <LoopModal
                    route="api_r_user_module_environmentalAspectsAssessment_assessment_xls"
                    value={{
                      assessmentHex: handleDownloadXls,
                      moduleBridgeHex,
                      plantHex,
                    }}
                    onError={() => {
                      AppNotificationAlert({
                        title: 'Error',
                        message: 'Se ha producido un error. Inténtelo de nuevo',
                        type: 'danger',
                        position: 'top-right',
                        duration: 3000,
                      });
                    }}
                    onFinish={(r) => {
                      setHandleDownloadXls(undefined);
                      window.location = r?.reportUrl;
                    }}
                  />
                )}
              </>
            ) : loading ? (
              <AppSpinner>
                <AppRemixIcon icon="loader-4" size={40} />
              </AppSpinner>
            ) : (
              <div className="alert alert-info">
                No se ha encontrado ninguna evaluación
              </div>
            )}
          </AppCard>
        </div>
      </main>
      <AppDeleteModal
        show={deleteModal}
        title="Eliminar evaluación"
        onConfirm={handleDelete}
        onClose={setDeleteModal}
      />
      <AppModal show={!!editModal} title="Editar evaluación" onClose={setEditModal}>
        <Formik
          initialValues={{
            title: editModal?.title || '',
          }}
          onSubmit={handleEdit}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12">
                  <AppInput
                    title="Nombre"
                    value={values?.title}
                    error={errors?.title}
                    onChange={(v) => setFieldValue('title', v)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-sm btn-success float-right mt-2"
                    disabled={isSubmitting}
                  >
                    Editar
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
